/**
 * Where the caller holds `manage_org` (Story 48, ADR-0033 §3, task 208).
 *
 * `/me` says only the home node ("this node and below"); the tree from
 * `/orgs` says what is below it. A rename, a new MSP or Client, users and
 * settings need `manage_org` at the node (`at`); suspending, moving,
 * offboarding and resale need it strictly above (`above`): an MSP does not
 * do those to itself.
 *
 * Before the tree exists the API treats `manage_org` as `manage_platform`
 * (task 205), and so does this.
 *
 * It decides which controls to show. The API refuses regardless.
 */

import { useMemo } from 'react';

import { subtreeIds } from '../lib/orgs';
import { useSelectedTenant } from './useSelectedTenant';
import { useTenant } from './useTenant';

export interface OrgAdmin {
  /** Holds `manage_org` anywhere (the Users tab, the export). */
  holds: boolean;
  /** The tree does not exist yet: the flat, pre-Story-48 screens apply. */
  preTree: boolean;
  homeId: string | null;
  at: (orgId: string) => boolean;
  above: (orgId: string) => boolean;
}

export function useOrgAdmin(): OrgAdmin {
  const { me, can } = useTenant();
  const { orgs } = useSelectedTenant();
  const grant = me?.permissions.manage_org;
  const preTree = orgs.homeId === null;
  const home = typeof grant === 'string' ? grant : null;

  return useMemo(() => {
    const holds = home !== null || (preTree && can('manage_platform'));
    const below = home && !preTree ? subtreeIds(orgs, home) : new Set<string>();
    const at = (orgId: string) => (preTree ? holds : home !== null && below.has(orgId));
    return {
      holds,
      preTree,
      homeId: home,
      at,
      above: (orgId: string) => at(orgId) && orgId !== home,
    };
    // `can` is a fresh closure each render; `me` is what it reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [home, preTree, orgs, me]);
}
